import React, { useCallback, useEffect, useState } from 'react';
import { Layers, Search } from 'lucide-react';
import EntityLink from '../components/EntityLink';
import SiteHeader from '../components/SiteHeader';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const PAGE = 500;

// One page for every catalogue hub — they share a shape (entity + count), so
// five near-identical files would only be five places to fix a bug.
//
// Search goes to the SERVER. The browse view holds the biggest 500, but e.g.
// ~7.4k franchises and far more people qualify, so filtering the loaded list
// would report "no match" for thousands that exist.
export default function Hub({ hub }) {
  const [items, setItems] = useState([]);
  const [matched, setMatched] = useState(0);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');

  const load = useCallback(async (q) => {
    setLoading(true);
    setError('');
    try {
      const qs = new URLSearchParams({ limit: String(PAGE) });
      if (q) qs.set('q', q);
      const r = await fetch(`${API_BASE}${hub.endpoint}?${qs}`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const json = await r.json();
      setItems(json.results || []);
      setMatched(json.matched ?? (json.results || []).length);
      setTruncated(Boolean(json.truncated));
    } catch (e) {
      setError(`Couldn't load ${hub.label.toLowerCase()}. Please try again.`);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [hub]);

  // Debounced so typing does not fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => load(query.trim()), query ? 250 : 0);
    return () => clearTimeout(t);
  }, [query, load]);

  // Reset when navigating between hubs, or the previous hub's rows flash.
  useEffect(() => { setQuery(''); setItems([]); }, [hub.path]);

  const unit = (n) => `${n} ${hub.unit}${n === 1 ? '' : 's'}`;

  return (
    <div className="min-h-screen bg-gray-50">
      <SiteHeader />
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="flex items-center gap-2 mb-1">
          <Layers className="w-6 h-6 text-videbits-600" />
          <h1 className="text-2xl font-bold text-gray-900">{hub.heading}</h1>
        </div>
        <p className="text-gray-600 mb-6">{hub.blurb}</p>

        <div className="relative mb-6">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={`Search all ${hub.label.toLowerCase()}…`}
            aria-label={`Search ${hub.label}`}
            className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-videbits-400"
          />
        </div>

        {loading && <p className="text-gray-500">Loading…</p>}

        {!loading && error && (
          <div className="bg-white rounded-xl p-6 text-center">
            <p className="text-red-600 mb-3">{error}</p>
            <button
              onClick={() => load(query.trim())}
              className="px-4 py-2 bg-videbits-600 text-white rounded-lg hover:bg-videbits-700"
            >
              Retry
            </button>
          </div>
        )}

        {!loading && !error && items.length === 0 && (
          <p className="text-gray-500">
            {query
              ? `Nothing matches “${query}”.`
              : `No ${hub.label.toLowerCase()} yet.`}
          </p>
        )}

        {!loading && !error && items.length > 0 && (
          <>
            <p className="text-xs text-gray-400 mb-3">
              {query
                ? `${matched.toLocaleString()} match${matched === 1 ? '' : 'es'}`
                : `Top ${items.length.toLocaleString()} by count`}
              {truncated && ' — refine to narrow'}
            </p>
            <ul className="bg-white rounded-xl shadow-sm divide-y divide-gray-100">
              {items.map((e) => (
                <li key={e.id}>
                  <EntityLink
                    target={hub.detail(e)}
                    className="flex items-center justify-between px-4 py-3 hover:bg-videbits-50 transition-colors"
                  >
                    <span className="text-gray-900">{e.name}</span>
                    <span className="text-xs text-gray-500 shrink-0 ml-4">
                      {unit(e.titles)}
                    </span>
                  </EntityLink>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  );
}
